import React from "react";
import { Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ERROR_COPY } from "@atlas/shared";
import type {
  AnalysedPoint,
  NearMiss,
  RouteAnalysis,
  RouteAnalysisAchievement,
  RouteAnalyserAllowance,
} from "@atlas/shared";
import {
  Award,
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  Crosshair,
  Eye,
  EyeOff,
  FileUp,
  Link2,
  Lock,
  Mountain,
  Route as RouteIcon,
  Share2,
  Sparkles,
} from "lucide-react";
import {
  analyseRoute,
  createRouteShare,
  fetchRouteAnalyserAllowance,
  isCrossedSection,
  RouteAnalyserError,
  ROUTE_ANALYSER_COPY,
  type AnalyseRouteOptions,
} from "../api/route-analyser";
import { AtlasPanel } from "../components/AtlasPanel";
import { TIER } from "../components/AchievementBadge";
import { Badge } from "../components/Badge";
import { Button } from "../components/Button";
import { TurnstileWidget, TURNSTILE_SITE_KEY } from "../components/TurnstileWidget";
import { RouteAnalysisMap, trailSectionState, type RouteMapFocus } from "../components/map/RouteAnalysisMap";
import { useAuth } from "../hooks/useAuth";
import { useIsPro } from "../hooks/useIsPro";
import { PRO_MONTHLY_GBP } from "../lib/pricing";
import { useFeatureFlags } from "../hooks/useFeatureFlags";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { usePageMeta } from "../hooks/usePageMeta";
import { landmarkCategoryIcon, landmarkCategoryLabel } from "../lib/landmark-icons";
import { EXPORT_INSTRUCTIONS } from "./ConnectedSourcesPage";

// Route analyser (spec 27 / ATL-126). Checks a route planned elsewhere and
// shows what it would collect. Atlas never edits, draws or exports routes:
// anything that would is a positioning change (see DECISIONS.md).
//
// Personal-only fields (isNew, onRecord, fractionBefore, newExploredCells,
// coverageBefore/After) are absent on generic and anonymous results. Absent
// means unknown: always test `=== true` / `=== false`, never `!isNew`.

export const ALLOWANCE_KEY = ["route-analyser/allowance"];

// Not re-exported from the @atlas/shared index (yet); derived from the contract.
type RouteAnalysisCollection = RouteAnalysis["collects"]["collections"][number];

// Map or drop zone on the left, results or "What you'll see" on the right.
const FRAME_COLS = "minmax(0, 1.2fr) minmax(0, 1fr)";

// ─── Formatting ───────────────────────────────────────────────────────────────

function fmtM(m: number): string {
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m).toLocaleString("en-GB")} m`;
}

function fmtKm(m: number): string {
  return `${(m / 1000).toFixed(1)} km`;
}

function plural(n: number, one: string, many?: string): string {
  return `${n.toLocaleString("en-GB")} ${n === 1 ? one : (many ?? `${one}s`)}`;
}

function pluralNoun(word: string): string {
  return /[^aeiou]y$/.test(word) ? `${word.slice(0, -1)}ies` : `${word}s`;
}

function withArticle(word: string): string {
  return `${/^[aeiou]/i.test(word) ? "an" : "a"} ${word}`;
}

function joinList(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? "";
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

function fmtResetDate(iso: string | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", timeZone: "Europe/London" });
}

function fmtPct(v: number): string {
  return `${v.toFixed(1)}%`;
}

function detourCopy(n: NearMiss<AnalysedPoint>): string {
  const climb = n.climbM != null && n.climbM > 0 ? `, ${Math.round(n.climbM)} m up` : "";
  return `About ${fmtM(n.detourM)} there and back${climb}`;
}

// ─── Summary line ─────────────────────────────────────────────────────────────

function summaryLine(a: RouteAnalysis): string {
  const parts: string[] = [];
  const peaks = a.collects.peaks.length;
  if (peaks > 0) parts.push(peaks === 1 ? "1 peak" : plural(peaks, "peak"));

  const byCategory = new Map<string, number>();
  for (const l of a.collects.landmarks) {
    const label = landmarkCategoryLabel(l.category).toLowerCase();
    byCategory.set(label, (byCategory.get(label) ?? 0) + 1);
  }
  for (const [label, n] of byCategory) parts.push(n === 1 ? withArticle(label) : `${n} ${pluralNoun(label)}`);

  // Spec 27 V9: paths the route only crosses never reach the summary.
  const completes = a.collects.trailSections.filter((s) => s.completes && !isCrossedSection(s)).length;

  let first: string;
  if (parts.length === 0 && completes === 0) {
    first = "This route wouldn't collect any peaks, landmarks or trail sections.";
  } else {
    const collectBit = parts.length > 0 ? `collect ${joinList(parts)}` : "";
    const sectionBit = completes > 0 ? `complete ${plural(completes, "trail section")}` : "";
    first = `This route would ${[collectBit, sectionBit].filter(Boolean).join(" and ")}.`;
    if (a.tier === "personal") {
      const fresh = [...a.collects.peaks, ...a.collects.landmarks].filter((p) => p.isNew === true).length;
      const total = peaks + a.collects.landmarks.length;
      if (total > 0) {
        first =
          fresh === 0
            ? `${first.slice(0, -1)}, all already on your record.`
            : fresh === total
              ? `${first.slice(0, -1)}, all new to you.`
              : `${first.slice(0, -1)}, ${fresh} of them new to you.`;
      }
    }
  }

  const misses = [...a.nearMisses.peaks, ...a.nearMisses.landmarks]
    .filter((n) => n.onRecord !== true)
    .sort((x, y) => x.distanceM - y.distanceM);
  const nearest = misses[0];
  if (!nearest) return first;
  const more = misses.length - 1;
  return `${first} It misses ${nearest.item.name} by ${fmtM(nearest.distanceM)}${more > 0 ? `, and ${more} more narrowly` : ""}.`;
}

// ─── Compare versions ─────────────────────────────────────────────────────────

interface Counts {
  peaks: number;
  landmarks: number;
  sections: number;
  near: number;
  distanceM: number;
}

function countsOf(a: RouteAnalysis): Counts {
  return {
    peaks: a.collects.peaks.length,
    landmarks: a.collects.landmarks.length,
    sections: a.collects.trailSections.filter((s) => s.completes).length,
    near:
      a.nearMisses.peaks.length +
      a.nearMisses.landmarks.length +
      a.nearMisses.trailSections.filter((s) => !isCrossedSection(s)).length,
    distanceM: a.route.distanceM,
  };
}

function deltaChips(prev: RouteAnalysis, next: RouteAnalysis): { label: string; good: boolean }[] {
  const p = countsOf(prev);
  const n = countsOf(next);
  const chips: { label: string; good: boolean }[] = [];
  const more = (d: number, one: string, many: string) =>
    d > 0 ? `+${d} ${d === 1 ? one : many}` : `${-d} fewer ${d === -1 ? one : many}`;
  const dp = n.peaks - p.peaks;
  if (dp) chips.push({ label: more(dp, "peak", "peaks"), good: dp > 0 });
  const dl = n.landmarks - p.landmarks;
  if (dl) chips.push({ label: more(dl, "landmark", "landmarks"), good: dl > 0 });
  const ds = n.sections - p.sections;
  if (ds) chips.push({ label: more(ds, "completed section", "completed sections"), good: ds > 0 });
  const dn = n.near - p.near;
  if (dn) chips.push({ label: more(dn, "near miss", "near misses"), good: dn < 0 });
  const dd = n.distanceM - p.distanceM;
  if (Math.abs(dd) >= 100) chips.push({ label: `${dd > 0 ? "+" : "-"}${fmtKm(Math.abs(dd))}`, good: true });
  return chips;
}

// ─── Small building blocks ────────────────────────────────────────────────────

const rowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "9px 0",
  borderBottom: "1px solid var(--border-subtle)",
  minWidth: 0,
};

const iconWell = (tone: "collect" | "record" | "near"): React.CSSProperties => ({
  width: 30,
  height: 30,
  flexShrink: 0,
  borderRadius: "50%",
  display: "grid",
  placeItems: "center",
  background: tone === "collect" ? "var(--success-soft)" : tone === "near" ? "var(--info-soft)" : "var(--surface-raised)",
  color: tone === "collect" ? "var(--status-success)" : tone === "near" ? "var(--status-info)" : "var(--text-faint)",
});

function ItemIcon({ p, kind }: { p: AnalysedPoint; kind: "peak" | "landmark" }) {
  return kind === "peak" ? <Mountain size={15} /> : <>{landmarkCategoryIcon(p.category, 15)}</>;
}

function ItemName({ p, kind }: { p: AnalysedPoint; kind: "peak" | "landmark" }) {
  const style: React.CSSProperties = {
    fontSize: 14,
    fontWeight: 600,
    color: "var(--text-primary)",
    textDecoration: "none",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "block",
  };
  return kind === "peak" ? (
    <Link to="/peaks/$slug" params={{ slug: p.slug }} style={style}>
      {p.name}
    </Link>
  ) : (
    <Link to="/landmarks/$slug" params={{ slug: p.slug }} style={style}>
      {p.name}
    </Link>
  );
}

function itemMeta(p: AnalysedPoint, kind: "peak" | "landmark"): string {
  if (kind === "peak") return p.elevationM != null ? `Peak · ${Math.round(p.elevationM).toLocaleString("en-GB")} m` : "Peak";
  return landmarkCategoryLabel(p.category);
}

function Collapsible({ title, count, children }: { title: string; count: number; children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false);
  if (count === 0) return null;
  return (
    <div style={{ marginTop: 10 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          background: "none",
          border: "none",
          padding: "4px 0",
          cursor: "pointer",
          color: "var(--text-secondary)",
          fontFamily: "var(--font-sans)",
          fontSize: 13,
          fontWeight: 600,
        }}
      >
        {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        {title} <span className="tnum" style={{ color: "var(--text-muted)" }}>({count})</span>
      </button>
      {open && <div>{children}</div>}
    </div>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.5 }}>{children}</div>;
}

// ─── Drop zone ────────────────────────────────────────────────────────────────

function DropZone({
  compact,
  busy,
  onFile,
  onRejected,
}: {
  compact: boolean;
  busy: boolean;
  onFile: (file: File) => void;
  onRejected: () => void;
}) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = React.useState(false);
  const depth = React.useRef(0);

  const take = (files: FileList | File[] | null | undefined) => {
    const list = Array.from(files ?? []);
    if (list.length === 0) return;
    const gpx = list.find((f) => f.name.toLowerCase().endsWith(".gpx"));
    if (gpx) onFile(gpx);
    else onRejected();
  };

  const exportLine = EXPORT_INSTRUCTIONS.filter((e) => ["Komoot", "OS Maps", "AllTrails"].includes(e.service));

  return (
    <div
      onDragEnter={(e) => {
        e.preventDefault();
        depth.current += 1;
        setDragging(true);
      }}
      onDragOver={(e) => e.preventDefault()}
      onDragLeave={(e) => {
        e.preventDefault();
        depth.current -= 1;
        if (depth.current <= 0) {
          depth.current = 0;
          setDragging(false);
        }
      }}
      onDrop={(e) => {
        e.preventDefault();
        depth.current = 0;
        setDragging(false);
        if (!busy) take(e.dataTransfer.files);
      }}
      style={{
        background: dragging ? "var(--info-soft)" : "var(--surface-card)",
        border: `1px dashed ${dragging ? "var(--status-info)" : "var(--border-strong)"}`,
        borderRadius: "var(--radius-lg)",
        padding: compact ? 16 : "32px 24px",
        display: "flex",
        flexDirection: compact ? "row" : "column",
        alignItems: "center",
        // Wide empty state: fill the column beside "What you'll see".
        justifyContent: "center",
        flex: compact ? undefined : 1,
        textAlign: compact ? "left" : "center",
        gap: compact ? 12 : 14,
        minWidth: 0,
        transition: "background var(--dur-quick, 180ms) ease",
      }}
      className={compact ? "r-wrap" : undefined}
    >
      <span
        style={{
          width: compact ? 36 : 48,
          height: compact ? 36 : 48,
          flexShrink: 0,
          borderRadius: "var(--radius-md)",
          background: "var(--surface-sunken)",
          border: "1px solid var(--border-subtle)",
          display: "grid",
          placeItems: "center",
          color: dragging ? "var(--status-info)" : "var(--text-muted)",
        }}
      >
        <FileUp size={compact ? 17 : 22} />
      </span>
      <div style={{ flex: compact ? 1 : undefined, minWidth: 0, maxWidth: compact ? undefined : 560 }}>
        {compact ? (
          <>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>Check another version</div>
            <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 2 }}>
              Change the route in your planner, export it again and drop it here. We'll show what changed.
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 16, fontWeight: 600, color: "var(--text-primary)", lineHeight: 1.45 }}>
              Drop a GPX of a route you've planned.
            </div>
            <div style={{ fontSize: 14, color: "var(--text-secondary)", marginTop: 6, lineHeight: 1.5 }}>
              We'll show what it would put on your record, and what it narrowly misses. Nothing is saved unless you share it.
            </div>
          </>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept=".gpx,application/gpx+xml"
        style={{ display: "none" }}
        onChange={(e) => {
          take(e.target.files);
          e.target.value = "";
        }}
      />
      <Button
        variant="secondary"
        size={compact ? "sm" : "md"}
        disabled={busy}
        leftIcon={<FileUp size={15} />}
        onClick={() => inputRef.current?.click()}
      >
        {busy ? "Checking..." : compact ? "Choose a file" : "Choose a GPX file"}
      </Button>
      {!compact && (
        <div style={{ fontSize: 12.5, color: "var(--text-muted)", lineHeight: 1.6, maxWidth: 620 }}>
          Export from{" "}
          {exportLine.map((e, i) => (
            <React.Fragment key={e.service}>
              {i > 0 && (i === exportLine.length - 1 ? " or " : ", ")}
              <span style={{ fontWeight: 600, color: "var(--text-secondary)" }}>{e.service}</span> ({e.steps})
            </React.Fragment>
          ))}
          .
        </div>
      )}
    </div>
  );
}

// ─── Personal step ────────────────────────────────────────────────────────────

export function PersonalStep({
  result,
  signedIn,
  allowance,
  proViewer,
  busy,
  onRequest,
  signInNext = "/route-analyser",
}: {
  result: RouteAnalysis;
  signedIn: boolean;
  allowance: RouteAnalyserAllowance | null;
  proViewer: boolean;
  busy: boolean;
  onRequest: () => void;
  /** Where sign-in comes back to. */
  signInNext?: string;
}) {
  if (result.tier === "personal") return null;

  const card: React.CSSProperties = {
    background: "var(--surface-sunken)",
    border: "1px solid var(--border-subtle)",
    borderRadius: "var(--radius-lg)",
    padding: 16,
    display: "flex",
    alignItems: "center",
    gap: 14,
    minWidth: 0,
  };

  if (!signedIn) {
    return (
      <div className="r-wrap" style={card}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>Which of these are new to you?</div>
          <Muted>Sign in and Atlas checks the route against your own record: new peaks, trail sections and ground.</Muted>
        </div>
        <Link to="/login" search={{ next: signInNext }} style={{ textDecoration: "none" }}>
          <Button variant="secondary" size="sm">
            Sign in to see what's new to you
          </Button>
        </Link>
      </div>
    );
  }

  const spent =
    !proViewer &&
    ((result.personal.available === false && result.personal.reason === "monthly_limit") ||
      (allowance?.tier === "free" && allowance.remainingThisMonth <= 0));

  if (spent) {
    const resets = fmtResetDate(result.personal.resetsAt ?? allowance?.resetsAt);
    return (
      <div className="r-wrap" style={card}>
        <span style={iconWell("record")}>
          <Lock size={15} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>
            You've used this month's personal check
          </div>
          <Muted>
            Pro checks are unlimited, from £{PRO_MONTHLY_GBP} a month, and add the achievements a route would unlock and
            your score after the walk.{resets ? ` Your free check comes back on ${resets}.` : ""}
          </Muted>
        </div>
        <Link to="/plan" style={{ textDecoration: "none" }}>
          <Button variant="secondary" size="sm">
            See Pro
          </Button>
        </Link>
      </div>
    );
  }

  const remaining = allowance?.tier === "free" ? allowance.remainingThisMonth : result.personal.remainingThisMonth;
  return (
    <div className="r-wrap" style={card}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>Which of these are new to you?</div>
        <Muted>
          Checks this route against your own record.
          {!proViewer && remaining != null ? ` ${remaining.toLocaleString("en-GB")} left this month.` : ""}
        </Muted>
      </div>
      <Button variant="secondary" size="sm" disabled={busy} leftIcon={<Sparkles size={14} />} onClick={onRequest}>
        See what's new to you
      </Button>
    </div>
  );
}

// ─── Results sections ─────────────────────────────────────────────────────────

function PointRow({
  p,
  kind,
  tone,
  right,
}: {
  p: AnalysedPoint;
  kind: "peak" | "landmark";
  tone: "collect" | "record";
  right?: React.ReactNode;
}) {
  return (
    <div style={rowStyle}>
      <span style={iconWell(tone)}>
        <ItemIcon p={p} kind={kind} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <ItemName p={p} kind={kind} />
        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{itemMeta(p, kind)}</div>
      </div>
      {right}
    </div>
  );
}

function CollectsPanel({ a }: { a: RouteAnalysis }) {
  const items: { p: AnalysedPoint; kind: "peak" | "landmark" }[] = [
    ...a.collects.peaks.map((p) => ({ p, kind: "peak" as const })),
    ...a.collects.landmarks.map((p) => ({ p, kind: "landmark" as const })),
  ];
  const personal = a.tier === "personal";
  const fresh = personal ? items.filter((i) => i.p.isNew === true) : items;
  const onRecord = personal ? items.filter((i) => i.p.isNew === false) : [];
  // Personal result missing the flag on an item: unknown, list it plainly.
  const unknown = personal ? items.filter((i) => i.p.isNew !== true && i.p.isNew !== false) : [];

  return (
    <AtlasPanel title="Would collect" bodyStyle={{ padding: "6px 18px 14px" }}>
      {items.length === 0 ? (
        <div style={{ padding: "10px 0" }}>
          <Muted>No peaks or landmarks sit within reach of this line.</Muted>
        </div>
      ) : (
        <>
          {personal && fresh.length > 0 && (
            <div className="eyebrow" style={{ fontSize: 10, margin: "10px 0 2px" }}>
              New to you · {fresh.length}
            </div>
          )}
          {personal && fresh.length === 0 && (
            <div style={{ padding: "10px 0" }}>
              <Muted>Nothing new to you on this route.</Muted>
            </div>
          )}
          {[...fresh, ...unknown].map(({ p, kind }) => (
            <PointRow
              key={`${kind}-${p.id}`}
              p={p}
              kind={kind}
              tone="collect"
              right={p.isNew === true ? <Badge variant="success">New to you</Badge> : undefined}
            />
          ))}
          <Collapsible title="Already on your record" count={onRecord.length}>
            {onRecord.map(({ p, kind }) => (
              <PointRow key={`${kind}-${p.id}`} p={p} kind={kind} tone="record" />
            ))}
          </Collapsible>
        </>
      )}
    </AtlasPanel>
  );
}

function NearMissRow({
  n,
  kind,
  onFocus,
}: {
  n: NearMiss<AnalysedPoint>;
  kind: "peak" | "landmark";
  onFocus: () => void;
}) {
  return (
    <div style={rowStyle}>
      <span style={iconWell(n.onRecord === true ? "record" : "near")}>
        <ItemIcon p={n.item} kind={kind} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <ItemName p={n.item} kind={kind} />
        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
          <span className="tnum" style={{ color: "var(--text-secondary)", fontWeight: 600 }}>
            Missed by {fmtM(n.distanceM)}
          </span>
          {" · "}
          {detourCopy(n)}
        </div>
      </div>
      <Button variant="ghost" size="sm" onClick={onFocus} leftIcon={<Crosshair size={14} />} aria-label={`Show ${n.item.name} on the map`}>
        Show
      </Button>
    </div>
  );
}

function NearMissPanel({ a, onFocus }: { a: RouteAnalysis; onFocus: (coords: [number, number][]) => void }) {
  const all = [
    ...a.nearMisses.peaks.map((n) => ({ n, kind: "peak" as const })),
    ...a.nearMisses.landmarks.map((n) => ({ n, kind: "landmark" as const })),
  ].sort((x, y) => x.n.distanceM - y.n.distanceM);
  const open = all.filter((x) => x.n.onRecord !== true);
  const onRecord = all.filter((x) => x.n.onRecord === true);

  return (
    <AtlasPanel title="Near misses" bodyStyle={{ padding: "6px 18px 14px" }}>
      {all.length === 0 ? (
        <div style={{ padding: "10px 0" }}>
          <Muted>Nothing just out of reach. Every summit and landmark near the line is either collected or well clear.</Muted>
        </div>
      ) : (
        <>
          <div style={{ padding: "8px 0 2px" }}>
            <Muted>Just outside the match radius. A short spur off the route would put them on your record.</Muted>
          </div>
          {open.map(({ n, kind }) => (
            <NearMissRow key={`${kind}-${n.item.id}`} n={n} kind={kind} onFocus={() => onFocus(n.spur.coordinates)} />
          ))}
          <Collapsible title="Near misses already on your record" count={onRecord.length}>
            {onRecord.map(({ n, kind }) => (
              <NearMissRow key={`${kind}-${n.item.id}`} n={n} kind={kind} onFocus={() => onFocus(n.spur.coordinates)} />
            ))}
          </Collapsible>
        </>
      )}
    </AtlasPanel>
  );
}

function SectionBar({ before, after }: { before?: number; after: number }) {
  const b = before != null ? Math.max(0, Math.min(1, before)) : 0;
  const f = Math.max(0, Math.min(1, after));
  return (
    <div
      aria-hidden="true"
      style={{
        position: "relative",
        height: 5,
        borderRadius: 999,
        background: "var(--surface-raised)",
        overflow: "hidden",
        marginTop: 6,
      }}
    >
      <div style={{ position: "absolute", inset: 0, width: `${f * 100}%`, background: "var(--status-success)", opacity: 0.85 }} />
      {before != null && (
        <div style={{ position: "absolute", inset: 0, width: `${b * 100}%`, background: "var(--text-faint)" }} />
      )}
    </div>
  );
}

// Spec 27 V9: trail rows describe the state of the record after the walk,
// never what the route itself covers. On a personal run the first touch of a
// section credits the user's whole history, so fractionAfter can exceed what
// the route walks. Generic and anonymous runs use a throwaway record, so they
// say "after this walk" without "on your record".
function sectionAfterCopy(a: RouteAnalysis, fractionAfter: number): string {
  const pct = `${Math.round(fractionAfter * 100)}%`;
  return a.tier === "personal" ? `${pct} on your record after this walk` : `${pct} after this walk`;
}

function SectionLink({ s }: { s: { trailSlug: string; sectionName: string } }) {
  return (
    <Link
      to="/trails/$id"
      params={{ id: s.trailSlug }}
      style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)", textDecoration: "none" }}
    >
      {s.sectionName}
    </Link>
  );
}

// Spec 33: an official alternate counts towards the trail % as you walk it
// (scaled onto the stretch it replaces); at 80% it completes that stretch.
function alternateCopy(alt: RouteAnalysis["trails"][number]["alternates"][number]): string {
  const pct = `${Math.round(alt.fractionAfter * 100)}%`;
  if (alt.kind === "extra") {
    return alt.completes ? "(optional loop) walked." : `(optional loop) ${pct} walked. It never counts towards the trail.`;
  }
  if (alt.completedBefore === true) return `(instead of ${alt.mainName}) is already on your record.`;
  if (alt.completes) return `(instead of ${alt.mainName}) walked: it counts for that stretch of the trail.`;
  return `(instead of ${alt.mainName}) ${pct} walked. It counts towards the trail as you go; at 80% it completes that stretch.`;
}

/** One whole trail: how far along the whole trail this walk leaves you. */
function TrailOverviewRow({
  t,
  onFocus,
}: {
  t: RouteAnalysis["trails"][number];
  onFocus: (coords: [number, number][]) => void;
}) {
  const personal = t.pctBefore != null;
  const walked = t.sections.filter((s) => trailSectionState(s) === "collected").length;
  const part = t.sections.filter((s) => trailSectionState(s) === "partial").length;
  const coords = t.sections.flatMap((s) => s.geojson.coordinates.flat());
  const sectionsLine =
    `${t.completedAfter.toLocaleString("en-GB")} of ${plural(t.totalSections, "section")} complete` +
    (walked > 0 ? ` · ${walked.toLocaleString("en-GB")} from this walk` : "") +
    (part > 0 ? ` · ${part.toLocaleString("en-GB")} partly walked` : "");
  return (
    <div style={{ ...rowStyle, alignItems: "flex-start" }}>
      <span style={iconWell("collect")}>
        <RouteIcon size={15} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Link
          to="/trails/$id"
          params={{ id: t.slug }}
          style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)", textDecoration: "none" }}
        >
          {t.name}
        </Link>
        <div style={{ fontSize: 13, color: "var(--text-secondary)", marginTop: 2 }}>
          {personal ? (
            <>
              <span className="tnum" style={{ fontWeight: 600, color: "var(--text-primary)" }}>
                {t.pctBefore}% to {t.pctAfter}%
              </span>{" "}
              of the whole trail after this walk
            </>
          ) : (
            <>
              This walk alone is{" "}
              <span className="tnum" style={{ fontWeight: 600, color: "var(--text-primary)" }}>
                {t.pctAfter}%
              </span>{" "}
              of the whole trail
            </>
          )}
        </div>
        <div className="tnum" style={{ fontSize: 12, color: "var(--text-muted)" }}>
          {sectionsLine}
        </div>
        <SectionBar before={personal ? (t.pctBefore ?? 0) / 100 : undefined} after={t.pctAfter / 100} />
        {(t.alternates ?? [])
          .filter((alt) => alt.touched)
          .map((alt) => (
            <div key={alt.id} style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 6, lineHeight: 1.5 }}>
              <span style={{ color: "var(--text-secondary)", fontWeight: 600 }}>{alt.name}</span>{" "}
              {alternateCopy(alt)}
            </div>
          ))}
      </div>
      {coords.length > 0 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onFocus(coords)}
          leftIcon={<Crosshair size={14} />}
          aria-label={`Show the whole ${t.name} on the map`}
        >
          Whole trail
        </Button>
      )}
    </div>
  );
}

function TrailPanel({ a, onFocus }: { a: RouteAnalysis; onFocus: (coords: [number, number][]) => void }) {
  const completes = a.collects.trailSections.filter((s) => s.completes);
  const shortIds = new Set(a.nearMisses.trailSections.map((s) => s.id));
  const short = a.nearMisses.trailSections.filter((s) => !isCrossedSection(s));
  // Touched, not complete, and not in the (capped) near-miss list.
  const partial = a.collects.trailSections.filter((s) => !s.completes && !shortIds.has(s.id));
  const crossed = [
    ...a.nearMisses.trailSections.filter((s) => isCrossedSection(s)),
    ...partial.filter((s) => isCrossedSection(s)),
  ];
  const partialShown = partial.filter((s) => !isCrossedSection(s));
  const before = new Map(a.collects.trailSections.map((s) => [s.id, s.fractionBefore]));
  const trails = a.trails ?? [];
  if (
    trails.length === 0 &&
    completes.length === 0 &&
    short.length === 0 &&
    partialShown.length === 0 &&
    crossed.length === 0
  )
    return null;

  const pct = (f: number) => `${Math.round(f * 100)}%`;
  const personal = a.tier === "personal";

  return (
    <AtlasPanel title="Trail sections" bodyStyle={{ padding: "6px 18px 14px" }}>
      {trails.map((t) => (
        <TrailOverviewRow key={t.id} t={t} onFocus={onFocus} />
      ))}
      {completes.map((s) => (
        <div key={s.id} style={{ ...rowStyle, alignItems: "flex-start" }}>
          <span style={iconWell(s.isNew === false ? "record" : "collect")}>
            <RouteIcon size={15} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <SectionLink s={s} />
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
              {s.trailName} ·{" "}
              {s.isNew === false
                ? "already complete on your record"
                : personal
                  ? `complete on your record after this walk${s.fractionBefore != null ? ` (${pct(s.fractionBefore)} → ${pct(s.fractionAfter)})` : ""}`
                  : "complete after this walk"}
            </div>
            <SectionBar before={s.fractionBefore} after={s.fractionAfter} />
          </div>
          {s.isNew === true ? <Badge variant="success">New to you</Badge> : s.isNew === false ? <Badge>On your record</Badge> : null}
        </div>
      ))}
      {short.map((s) => {
        const coords = s.gaps.coordinates.flat();
        const b = before.get(s.id);
        return (
          <div key={s.id} style={{ ...rowStyle, alignItems: "flex-start" }}>
            <span style={iconWell("near")}>
              <RouteIcon size={15} />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <SectionLink s={s} />
              <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                {s.trailName} · {sectionAfterCopy(a, s.fractionAfter)}
                {b != null && b > 0 ? ` (was ${pct(b)})` : ""} ·{" "}
                <span className="tnum" style={{ color: "var(--text-secondary)", fontWeight: 600 }}>
                  {fmtM(s.missingM)}
                </span>{" "}
                to go
              </div>
              <SectionBar before={b} after={s.fractionAfter} />
            </div>
            {coords.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onFocus(coords)}
                leftIcon={<Crosshair size={14} />}
                aria-label={`Show the gap on ${s.sectionName}`}
              >
                Gap
              </Button>
            )}
          </div>
        );
      })}
      {partialShown.map((s) => (
        <div key={s.id} style={rowStyle}>
          <span style={iconWell("record")}>
            <RouteIcon size={15} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <SectionLink s={s} />
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
              {s.trailName} · {sectionAfterCopy(a, s.fractionAfter)}
            </div>
          </div>
        </div>
      ))}
      <Collapsible title="Paths this route only crosses" count={crossed.length}>
        {crossed.map((s) => (
          <div key={s.id} style={rowStyle}>
            <span style={iconWell("record")}>
              <RouteIcon size={15} />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <SectionLink s={s} />
              <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                {s.trailName} · {sectionAfterCopy(a, s.fractionAfter)}
              </div>
            </div>
          </div>
        ))}
      </Collapsible>
    </AtlasPanel>
  );
}

function GroundPanel({ a }: { a: RouteAnalysis }) {
  const cells = a.collects.newExploredCells;
  if (a.collects.regions.length === 0 && cells == null) return null;
  return (
    <AtlasPanel title="New ground" bodyStyle={{ padding: "6px 18px 14px" }}>
      {cells != null && (
        <div style={{ ...rowStyle }}>
          <div style={{ flex: 1, minWidth: 0, fontSize: 14, color: "var(--text-primary)" }}>
            <span className="tnum" style={{ fontWeight: 700, fontFamily: "var(--font-display)" }}>
              {cells.toLocaleString("en-GB")}
            </span>{" "}
            new explored {cells === 1 ? "cell" : "cells"} on your map
          </div>
        </div>
      )}
      {a.collects.regions.map((r) => (
        <div key={r.id} style={rowStyle}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Link
              to="/regions/$slug"
              params={{ slug: r.slug }}
              style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)", textDecoration: "none" }}
            >
              {r.name}
            </Link>
          </div>
          {r.coverageBefore != null && r.coverageAfter != null ? (
            <span className="tnum" style={{ fontSize: 13, color: "var(--text-secondary)", whiteSpace: "nowrap" }}>
              {fmtPct(r.coverageBefore)} → <strong style={{ color: "var(--text-primary)" }}>{fmtPct(r.coverageAfter)}</strong>
            </span>
          ) : (
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>Passes through</span>
          )}
        </div>
      ))}
    </AtlasPanel>
  );
}

// Collections the route would add to. Personal runs read the before/after
// snapshot ("47 to 49 of 214", "+2"); generic and anonymous runs only know
// how many of the collection sit on the route. No gold: completion here is a
// preview, not an unlock, and gold on this page belongs to "Would unlock".
function CollectionRow({ c }: { c: RouteAnalysisCollection }) {
  const before = c.countBefore;
  const after = c.countAfter;
  const personal = before != null && after != null;
  const gain = personal ? after - before : 0;
  return (
    <div style={{ ...rowStyle, alignItems: "flex-start" }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Link
          to="/collections/$slug"
          params={{ slug: c.slug }}
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: "var(--text-primary)",
            textDecoration: "none",
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {c.name}
        </Link>
        <div className="tnum" style={{ fontSize: 12, color: "var(--text-muted)" }}>
          {personal ? (
            <>
              {/* No gain: onRoute counts items already on the record (and, for
                  trail collections, trails this walk doesn't finish), so say
                  only what is true in both cases. */}
              {gain === 0 && <>{c.onRoute.toLocaleString("en-GB")} on this route · </>}
              {gain > 0 && <>{before.toLocaleString("en-GB")} to </>}
              <span style={{ color: "var(--text-secondary)", fontWeight: 600 }}>{after.toLocaleString("en-GB")}</span> of{" "}
              {c.total.toLocaleString("en-GB")}
              {gain === 0 ? " on your record" : ""}
              {after >= c.total
                ? gain > 0
                  ? " · complete after this walk"
                  : " · complete"
                : ` · ${(c.total - after).toLocaleString("en-GB")} to go`}
            </>
          ) : (
            <>
              <span style={{ color: "var(--text-secondary)", fontWeight: 600 }}>{c.onRoute.toLocaleString("en-GB")}</span> of{" "}
              {c.total.toLocaleString("en-GB")} on this route
            </>
          )}
        </div>
        {/* Personal only: without a record, a bar would read as progress. */}
        {personal && <SectionBar before={before / Math.max(c.total, 1)} after={after / Math.max(c.total, 1)} />}
      </div>
      {personal && gain > 0 && (
        <span className="tnum" style={{ flexShrink: 0 }}>
          <Badge variant="success">+{gain.toLocaleString("en-GB")}</Badge>
        </span>
      )}
    </div>
  );
}

function CollectionsPanel({ a }: { a: RouteAnalysis }) {
  const all = a.collects.collections;
  if (all.length === 0) return null;
  const unchanged = all.filter((c) => c.countBefore != null && c.countAfter === c.countBefore);
  const moving = all.filter((c) => !unchanged.includes(c));
  return (
    <AtlasPanel title="Collections" bodyStyle={{ padding: "6px 18px 14px" }}>
      {moving.length === 0 ? (
        <div style={{ padding: "10px 0" }}>
          <Muted>Everything this route would collect is already on your record.</Muted>
        </div>
      ) : (
        moving.map((c) => <CollectionRow key={c.id} c={c} />)
      )}
      <Collapsible title="No change" count={unchanged.length}>
        {unchanged.map((c) => (
          <CollectionRow key={c.id} c={c} />
        ))}
      </Collapsible>
    </AtlasPanel>
  );
}

const TIER_VARIANT: Record<string, "bronze" | "silver" | "gold" | "platinum" | "mythic"> = {
  bronze: "bronze",
  silver: "silver",
  gold: "gold",
  platinum: "platinum",
  mythic: "mythic",
};

// "Would unlock" rows open in place to say what each achievement is for, in
// the Achievements page's own type: description in its muted 13px body, tier
// and points in its mono caption. This list is the page's only gold.
function AchievementRow({ ach }: { ach: RouteAnalysisAchievement }) {
  const [open, setOpen] = React.useState(false);
  const panelId = `ra-ach-${ach.id}`;
  const tier = TIER[ach.tier as keyof typeof TIER];
  const tierLabel = ach.tier.charAt(0).toUpperCase() + ach.tier.slice(1);
  return (
    <div style={{ borderBottom: "1px solid var(--border-subtle)" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={panelId}
        style={{
          ...rowStyle,
          borderBottom: "none",
          width: "100%",
          background: "none",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
          fontFamily: "var(--font-sans)",
          color: "var(--text-primary)",
        }}
      >
        <span
          style={{
            ...iconWell("collect"),
            background: "var(--accent-soft)",
            color: "var(--gold-400)",
            boxShadow: "var(--glow-gold-sm)",
          }}
        >
          <Sparkles size={15} />
        </span>
        <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>
          {ach.name}
        </span>
        <Badge variant={TIER_VARIANT[ach.tier] ?? "gold"}>{tierLabel}</Badge>
        <span className="tnum" style={{ fontSize: 13, color: "var(--text-accent)", fontWeight: 600, whiteSpace: "nowrap" }}>
          +{ach.points.toLocaleString("en-GB")}
        </span>
        <span style={{ color: "var(--text-muted)", display: "grid", placeItems: "center", flexShrink: 0 }}>
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </span>
      </button>
      <div id={panelId} hidden={!open} style={{ padding: "0 0 12px 42px", minWidth: 0 }}>
        <div style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.4 }}>{ach.description}</div>
        <div
          style={{
            marginTop: 6,
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            textTransform: "uppercase",
            letterSpacing: ".1em",
            color: tier?.color ?? "var(--text-faint)",
          }}
        >
          {tierLabel} · {ach.points.toLocaleString("en-GB")} pts
        </div>
      </div>
    </div>
  );
}

function ProPanel({ a }: { a: RouteAnalysis }) {
  const pro = a.pro;
  if (!pro) return null;
  return (
    <AtlasPanel title="Would unlock" bodyStyle={{ padding: "6px 18px 14px" }}>
      {pro.achievements.length === 0 ? (
        <div style={{ padding: "10px 0" }}>
          <Muted>No achievements on this one.</Muted>
        </div>
      ) : (
        pro.achievements.map((ach) => <AchievementRow key={ach.id} ach={ach} />)
      )}
      <div className="r-wrap" style={{ display: "flex", gap: 24, padding: "12px 0 2px", flexWrap: "wrap" }}>
        <div>
          <div className="eyebrow" style={{ fontSize: 10 }}>
            Outdoor score after
          </div>
          <div className="tnum" style={{ fontSize: 15, color: "var(--text-secondary)", marginTop: 2 }}>
            {pro.scoreBefore.toLocaleString("en-GB")} →{" "}
            <strong style={{ color: "var(--text-primary)", fontFamily: "var(--font-display)" }}>
              {pro.scoreAfter.toLocaleString("en-GB")}
            </strong>
          </div>
        </div>
        <div>
          <div className="eyebrow" style={{ fontSize: 10 }}>
            Level after
          </div>
          <div className="tnum" style={{ fontSize: 15, color: "var(--text-secondary)", marginTop: 2 }}>
            {pro.levelBefore} → <strong style={{ color: "var(--text-primary)", fontFamily: "var(--font-display)" }}>{pro.levelAfter}</strong>
          </div>
        </div>
      </div>
      {pro.timeBoundOmitted && (
        <div style={{ paddingTop: 10 }}>
          <Muted>Time-based achievements depend on when you walk it, so they aren't shown.</Muted>
        </div>
      )}
    </AtlasPanel>
  );
}

function Legend({
  personal,
  hasTrails,
  nearbyCount,
  showNearby,
  onToggleNearby,
}: {
  personal: boolean;
  hasTrails: boolean;
  nearbyCount: number;
  showNearby: boolean;
  onToggleNearby: () => void;
}) {
  const dot = (bg: string, size = 10, opacity = 1): React.CSSProperties => ({
    width: size,
    height: size,
    borderRadius: "50%",
    background: bg,
    opacity,
    flexShrink: 0,
  });
  const dash = (c: string): React.CSSProperties => ({
    width: 16,
    height: 0,
    borderTop: `2px dashed ${c}`,
    flexShrink: 0,
  });
  const bar = (c: string, dashed = false): React.CSSProperties => ({
    width: 16,
    height: 0,
    borderTop: `4px ${dashed ? "dashed" : "solid"} ${c}`,
    flexShrink: 0,
  });
  const item = (swatch: React.ReactNode, label: string) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      {swatch}
      {label}
    </span>
  );
  return (
    <div
      className="r-wrap"
      style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px 16px", fontSize: 12, color: "var(--text-muted)" }}
    >
      {item(<span style={dot("var(--status-success)")} />, "Would collect")}
      {personal && item(<span style={dot("var(--text-faint)")} />, "Already on your record")}
      {item(<span style={dot("var(--status-info)")} />, "Near miss")}
      {item(<span style={dash("var(--status-info)")} />, "Spur or trail gap")}
      {hasTrails && item(<span style={bar("var(--status-success)")} />, "Section this walk completes")}
      {hasTrails && item(<span style={bar("var(--status-info)")} />, "Partly walked")}
      {hasTrails && personal && item(<span style={bar("var(--text-faint)")} />, "Section on your record")}
      {hasTrails && item(<span style={{ ...bar("var(--text-faint)", true), borderTopWidth: 2 }} />, "Rest of the trail")}
      {nearbyCount > 0 && (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          {item(<span style={dot("var(--text-faint)", 7, showNearby ? 0.8 : 0.35)} />, "Nearby")}
          <button
            type="button"
            onClick={onToggleNearby}
            aria-pressed={showNearby}
            title={showNearby ? "Hide nearby peaks and landmarks" : "Show nearby peaks and landmarks"}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              height: 24,
              padding: "0 8px",
              borderRadius: "var(--radius-pill)",
              border: "1px solid var(--border-default)",
              background: showNearby ? "var(--surface-raised)" : "transparent",
              color: "var(--text-secondary)",
              fontFamily: "var(--font-sans)",
              fontSize: 11.5,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {showNearby ? <EyeOff size={12} /> : <Eye size={12} />}
            {showNearby ? "Hide" : "Show"}
          </button>
        </span>
      )}
    </div>
  );
}

// ─── Empty state: what the check shows ────────────────────────────────────────

// ─── Results frame (shared with the shared-route page) ───────────────────────

export function routeFacts(a: RouteAnalysis): string {
  return (
    fmtKm(a.route.distanceM) +
    (a.route.elevationGainM != null ? ` · ${Math.round(a.route.elevationGainM).toLocaleString("en-GB")} m up` : "")
  );
}

/**
 * Map column + results column for one analysis. Used by this page and by
 * /routes/:id (RouteSharePage), so a shared link reads exactly like the check
 * its owner saw. Slots carry what differs between the two.
 */
export function AnalysisResults({
  result,
  focus,
  onFocus,
  pending = false,
  top,
  eyebrow,
  afterSummary,
  beforePanels,
  mapFooter,
  footnote,
}: {
  result: RouteAnalysis;
  focus: RouteMapFocus | null;
  onFocus: (coordinates: [number, number][]) => void;
  pending?: boolean;
  top?: React.ReactNode;
  eyebrow: React.ReactNode;
  afterSummary?: React.ReactNode;
  beforePanels?: React.ReactNode;
  /** Under the legend beside the map; moves below the results on narrow screens. */
  mapFooter?: React.ReactNode;
  footnote: string;
}) {
  const { user } = useAuth();
  const narrow = useMediaQuery("(max-width: 760px)");
  const [showNearby, setShowNearby] = React.useState(true);

  return (
    <div
      className="r-cols-1"
      style={{
        display: "grid",
        gridTemplateColumns: FRAME_COLS,
        gap: 20,
        alignItems: "start",
        minWidth: 0,
      }}
    >
      <div
        style={{
          position: narrow ? "static" : "sticky",
          top: user ? 0 : 16,
          display: "flex",
          flexDirection: "column",
          gap: 12,
          minWidth: 0,
        }}
      >
        <div
          style={{
            position: "relative",
            height: narrow ? 340 : "min(68vh, 640px)",
            borderRadius: "var(--radius-lg)",
            overflow: "hidden",
            border: "1px solid var(--border-subtle)",
            boxShadow: "var(--shadow-sm)",
            background: "var(--surface-sunken)",
          }}
        >
          <RouteAnalysisMap analysis={result} focus={focus} showNearby={showNearby} />
        </div>
        <Legend
          personal={result.tier === "personal"}
          hasTrails={(result.trails ?? []).length > 0}
          nearbyCount={result.nearby.peaks.length + result.nearby.landmarks.length}
          showNearby={showNearby}
          onToggleNearby={() => setShowNearby((v) => !v)}
        />
        {!narrow && mapFooter}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0, opacity: pending ? 0.6 : 1, transition: "opacity .15s ease" }}>
        {top}
        <div style={{ minWidth: 0 }}>
          <div className="eyebrow tnum" style={{ marginBottom: 6 }}>
            {eyebrow}
          </div>
          <p style={{ fontSize: 17, lineHeight: 1.5, color: "var(--text-primary)", margin: 0, fontWeight: 500 }}>
            {summaryLine(result)}
          </p>
          {afterSummary}
        </div>

        {beforePanels}

        <CollectsPanel a={result} />
        <NearMissPanel a={result} onFocus={onFocus} />
        <TrailPanel a={result} onFocus={onFocus} />
        <CollectionsPanel a={result} />
        <GroundPanel a={result} />
        <ProPanel a={result} />

        {narrow && mapFooter}

        <Muted>{footnote}</Muted>
      </div>
    </div>
  );
}

// ─── Share link ───────────────────────────────────────────────────────────────

const shareCard: React.CSSProperties = {
  background: "var(--surface-sunken)",
  border: "1px solid var(--border-subtle)",
  borderRadius: "var(--radius-lg)",
  padding: 16,
  display: "flex",
  alignItems: "center",
  gap: 14,
  minWidth: 0,
};

/** The full link for a /routes/:id path, on whichever host this page runs on. */
export function shareUrl(path: string): string {
  return `${window.location.origin}${path}`;
}

/** Copy, plus the system share sheet where the browser has one. */
export function CopyLinkButtons({ url, title }: { url: string; title: string }) {
  const [copied, setCopied] = React.useState(false);
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  const copy = () => {
    void navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      <Button variant="secondary" size="sm" leftIcon={copied ? <Check size={14} /> : <Copy size={14} />} onClick={copy}>
        {copied ? "Copied" : "Copy link"}
      </Button>
      {canShare && (
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Share2 size={14} />}
          onClick={() => void navigator.share({ title, url }).catch(() => undefined)}
        >
          Share
        </Button>
      )}
    </div>
  );
}

function ShareStep({ file, signedIn }: { file: File | null; signedIn: boolean }) {
  const [state, setState] = React.useState<
    { kind: "idle" } | { kind: "making" } | { kind: "made"; url: string } | { kind: "failed"; message: string }
  >({ kind: "idle" });
  if (!file) return null;

  const title = file.name.replace(/\.gpx$/i, "");

  if (!signedIn) {
    return (
      <div className="r-wrap" style={shareCard}>
        <span style={iconWell("near")}>
          <Link2 size={15} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>Walking it with others?</div>
          <Muted>Sign in to make a link to this check that anyone you're walking with can open.</Muted>
        </div>
        <Link to="/login" search={{ next: "/route-analyser" }} style={{ textDecoration: "none" }}>
          <Button variant="secondary" size="sm">
            Sign in to share
          </Button>
        </Link>
      </div>
    );
  }

  if (state.kind === "made") {
    return (
      <div className="r-wrap" style={shareCard}>
        <span style={iconWell("collect")}>
          <Link2 size={15} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)", overflowWrap: "anywhere" }}>
            <a href={state.url} target="_blank" rel="noreferrer" style={{ color: "var(--text-primary)" }}>
              {state.url.replace(/^https?:\/\//, "")}
            </a>
          </div>
          <Muted>
            Anyone with the link sees the route and what it collects, never your record. Signed in, they can check it
            against their own. You can stop sharing from the link's page.
          </Muted>
        </div>
        <CopyLinkButtons url={state.url} title={title} />
      </div>
    );
  }

  const make = async () => {
    setState({ kind: "making" });
    try {
      const created = await createRouteShare(file);
      setState({ kind: "made", url: shareUrl(created.path) });
    } catch (e) {
      setState({ kind: "failed", message: e instanceof RouteAnalyserError ? e.message : ERROR_COPY.generic });
    }
  };

  return (
    <div className="r-wrap" style={shareCard}>
      <span style={iconWell("near")}>
        <Link2 size={15} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>Walking it with others?</div>
        <Muted>
          {state.kind === "failed"
            ? state.message
            : "Make a link to this check. It saves the route line so others can open it; your record stays private."}
        </Muted>
      </div>
      <Button
        variant="secondary"
        size="sm"
        disabled={state.kind === "making"}
        leftIcon={<Link2 size={14} />}
        onClick={() => void make()}
      >
        {state.kind === "making" ? "Making link" : "Share this route"}
      </Button>
    </div>
  );
}

function WhatYoullSee() {
  const lines: { icon: React.ReactNode; title: string; body: string }[] = [
    {
      icon: <Mountain size={15} />,
      title: "What it would collect",
      body: "Peaks, landmarks and trail sections the line passes close enough to count.",
    },
    {
      icon: <Crosshair size={15} />,
      title: "Near misses",
      body: "Summits and landmarks just off the line, with the detour it would take to reach them.",
    },
    {
      icon: <Sparkles size={15} />,
      title: "What's new to you",
      body: "Signed in, the same check against your own record: new peaks, sections, collections and ground.",
    },
    {
      icon: <Award size={15} />,
      title: "With Pro, what it would unlock",
      body: "The achievements the walk would earn, and your score and level after it.",
    },
  ];
  return (
    <section
      aria-label="What you'll see"
      style={{
        background: "var(--surface-sunken)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-lg)",
        padding: "20px 22px",
        minWidth: 0,
      }}
    >
      <div className="eyebrow" style={{ fontSize: 10, marginBottom: 6 }}>
        What you'll see
      </div>
      {lines.map((l, i) => (
        <div
          key={l.title}
          style={{
            display: "flex",
            gap: 12,
            alignItems: "flex-start",
            padding: "12px 0",
            borderBottom: i < lines.length - 1 ? "1px solid var(--border-subtle)" : "none",
            minWidth: 0,
          }}
        >
          <span style={{ ...iconWell("record"), color: "var(--text-muted)" }}>{l.icon}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>{l.title}</div>
            <div style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.5, marginTop: 2 }}>{l.body}</div>
          </div>
        </div>
      ))}
    </section>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

type Analyse = (file: File, opts?: AnalyseRouteOptions) => Promise<RouteAnalysis>;

interface DevFixture {
  analyse: Analyse;
  signedIn: boolean;
  allowance: RouteAnalyserAllowance | null;
}

export default function RouteAnalyserPage() {
  usePageMeta({
    title: "Check a route before you walk it",
    description:
      "Drop in a GPX of a route you've planned and see which peaks, landmarks and trail sections it would collect, and what it narrowly misses. Nothing is saved unless you share it.",
    canonicalPath: "/route-analyser",
  });

  const qc = useQueryClient();
  const { user } = useAuth();
  const { isPro } = useIsPro();
  const flags = useFeatureFlags();

  const [fixture, setFixture] = React.useState<DevFixture | null>(null);
  const [result, setResult] = React.useState<RouteAnalysis | null>(null);
  const [file, setFile] = React.useState<File | null>(null);
  const [previous, setPrevious] = React.useState<{ result: RouteAnalysis; fileName: string } | null>(null);
  const [error, setError] = React.useState<RouteAnalyserError | null>(null);
  const [pending, setPending] = React.useState(false);
  const [focus, setFocus] = React.useState<RouteMapFocus | null>(null);
  const [turnstileToken, setTurnstileToken] = React.useState<string | null>(null);
  const [turnstileNonce, setTurnstileNonce] = React.useState(0);
  const [queued, setQueued] = React.useState<{ file: File; personal: boolean } | null>(null);

  const resultRef = React.useRef(result);
  resultRef.current = result;
  const fileRef = React.useRef(file);
  fileRef.current = file;

  // DEV ONLY: /route-analyser?fixture=<variant>. The branch is dead code in a
  // production build, so the fixture module is never bundled.
  React.useEffect(() => {
    if (!import.meta.env.DEV) return;
    const params = new URLSearchParams(window.location.search);
    const variant = params.get("fixture");
    if (!variant) return;
    void import("./route-analyser.fixture").then((m) => {
      const v = variant as Parameters<typeof m.fixtureViewer>[0];
      const viewer = m.fixtureViewer(v);
      setFixture({
        analyse: m.createFixtureAnalyser(v, params.get("fixtureError")),
        signedIn: viewer.signedIn,
        allowance: viewer.allowance,
      });
      const initial = m.fixtureInitial(v);
      if (initial) {
        setResult(initial);
        setFile(new File(["<gpx/>"], "langdale-pikes.gpx", { type: "application/gpx+xml" }));
      }
    });
  }, []);

  const signedIn = fixture ? fixture.signedIn : user != null;

  const allowanceQuery = useQuery({
    queryKey: ALLOWANCE_KEY,
    queryFn: fetchRouteAnalyserAllowance,
    enabled: user != null && !fixture,
    retry: false,
    staleTime: 60_000,
  });
  const allowance = fixture ? fixture.allowance : (allowanceQuery.data ?? null);

  // Personal is requested automatically only when we know the viewer is Pro
  // (or an admin). Never on a free account's behalf: that would spend their
  // one monthly check on a test upload.
  const proViewer = allowance ? allowance.tier === "pro" : !fixture && user != null && (user.isAdmin || isPro);

  const needsToken = !signedIn && !!TURNSTILE_SITE_KEY && !fixture;
  const publicClosed = !signedIn && !flags.routeAnalyserPublic;

  const submit = React.useCallback(
    async (next: File, personal: boolean) => {
      if (needsToken && !turnstileToken) {
        setQueued({ file: next, personal });
        return;
      }
      const isNewFile = next !== fileRef.current;
      const token = turnstileToken;
      const analyse: Analyse = fixture?.analyse ?? analyseRoute;
      setPending(true);
      setError(null);
      try {
        const r = await analyse(next, { personal, turnstileToken: needsToken ? token : null });
        const prior = resultRef.current;
        const priorFile = fileRef.current;
        // A new file is "another version": keep the last result to compare
        // against. The personal re-run of the same file just replaces it.
        if (isNewFile && prior && priorFile) setPrevious({ result: prior, fileName: priorFile.name });
        setResult(r);
        setFile(next);
        setFocus(null);
        if (personal) void qc.invalidateQueries({ queryKey: ALLOWANCE_KEY });
      } catch (e) {
        setError(e instanceof RouteAnalyserError ? e : new RouteAnalyserError("other", ERROR_COPY.generic));
      } finally {
        setPending(false);
        // Turnstile tokens are single-use: fresh challenge after every submit,
        // or the next upload (another version, or a retry) fails verification.
        if (needsToken) {
          setTurnstileToken(null);
          setTurnstileNonce((n) => n + 1);
        }
      }
    },
    [needsToken, turnstileToken, fixture, qc],
  );

  // A file dropped before the Turnstile check finished goes as soon as it has.
  React.useEffect(() => {
    if (queued && turnstileToken) {
      const q = queued;
      setQueued(null);
      void submit(q.file, q.personal);
    }
  }, [queued, turnstileToken, submit]);

  const onFile = (f: File) => void submit(f, proViewer);
  const onRejected = () =>
    setError(new RouteAnalyserError("invalid", "That isn't a GPX file. Export the route as GPX and drop it here."));
  const onFocus = (coordinates: [number, number][]) => setFocus({ nonce: Date.now(), coordinates });

  const busy = pending || queued != null;
  const chips = previous && result ? deltaChips(previous.result, result) : null;

  const errorBox = error && (
    <div
      role="alert"
      style={{
        background: "var(--danger-soft)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-md)",
        padding: "10px 14px",
        fontSize: 13.5,
        color: "var(--text-primary)",
        lineHeight: 1.5,
      }}
    >
      {error.message}
      {error.kind === "unavailable" && (
        <>
          {" "}
          <Link to="/login" search={{ next: "/route-analyser" }} style={{ color: "var(--text-primary)", fontWeight: 600 }}>
            Sign in
          </Link>
        </>
      )}
    </div>
  );

  const queuedNote = queued && (
    <Muted>Finishing the bot check below, then we'll check your route.</Muted>
  );

  const turnstile = needsToken && !publicClosed && (
    <TurnstileWidget onToken={setTurnstileToken} resetNonce={turnstileNonce} />
  );

  const closedLine = (
    <div
      style={{
        background: "var(--surface-sunken)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-lg)",
        padding: 18,
        fontSize: 14,
        color: "var(--text-secondary)",
      }}
    >
      {ROUTE_ANALYSER_COPY.unavailable}{" "}
      <Link to="/login" search={{ next: "/route-analyser" }} style={{ color: "var(--text-primary)", fontWeight: 600 }}>
        Sign in
      </Link>
    </div>
  );

  // Narrow screens stack the map column above the results, so "check another
  // version" moves below the results there instead of sitting above the summary.
  const anotherVersion = (
    <>
      {publicClosed ? closedLine : <DropZone compact busy={busy} onFile={onFile} onRejected={onRejected} />}
      {turnstile}
      {queuedNote}
    </>
  );

  return (
    <div
      className="r-page"
      // Signed in, AppShell gives this div a bounded height and it scrolls.
      // Signed out, the public chrome lets the window scroll, and overflow
      // here would make this div the sticky map's scroll box (it never
      // scrolls, so the map would scroll away with the results).
      style={{ padding: 28, display: "flex", flexDirection: "column", gap: 20, flex: 1, overflow: user ? "auto" : "visible", minWidth: 0 }}
    >
      {/* Full content width, like Dashboard and Collections (no page cap).
          One column frame for both states: the intro runs the width of the
          left column, the drop zone sits beside "What you'll see", and the
          results put the map there instead. One column at 760 px and below. */}
      <div className="r-cols-1" style={{ display: "grid", gridTemplateColumns: FRAME_COLS, gap: 20, minWidth: 0 }}>
        <header style={{ minWidth: 0 }}>
          <div className="eyebrow" style={{ marginBottom: 8 }}>
            Route check
          </div>
          <h1 style={{ font: "var(--type-h1)", color: "var(--text-primary)", margin: 0, letterSpacing: "-.01em" }}>
            Check a route before you walk it
          </h1>
          <p style={{ fontSize: 14.5, color: "var(--text-secondary)", margin: "8px 0 0", lineHeight: 1.55 }}>
            Route planners snap to paths, and summits often sit just off them. See what a route planned in Komoot, OS Maps or
            AllTrails would collect before you set off, and what it misses by a few metres.
          </p>
        </header>
      </div>

        {!result ? (
          <div
            className="r-cols-1"
            style={{ display: "grid", gridTemplateColumns: FRAME_COLS, gap: 20, alignItems: "stretch", minWidth: 0 }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
              {publicClosed ? closedLine : <DropZone compact={false} busy={busy} onFile={onFile} onRejected={onRejected} />}
              {turnstile}
              {queuedNote}
              {errorBox}
            </div>
            <WhatYoullSee />
          </div>
        ) : (
          <AnalysisResults
            result={result}
            focus={focus}
            onFocus={onFocus}
            pending={pending}
            mapFooter={anotherVersion}
            eyebrow={
              <>
                {file?.name ? `${file.name} · ` : ""}
                {routeFacts(result)}
              </>
            }
            afterSummary={
              <>
                {chips && previous && (
                  <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                    <span style={{ fontSize: 12, color: "var(--text-muted)", marginRight: 2 }}>
                      Against {previous.fileName}:
                    </span>
                    {chips.length === 0 ? (
                      <Badge>No change in what it would collect</Badge>
                    ) : (
                      chips.map((c) => (
                        <Badge key={c.label} variant={c.good ? "success" : "neutral"}>
                          {c.label}
                        </Badge>
                      ))
                    )}
                  </div>
                )}
              </>
            }
            top={errorBox}
            beforePanels={
              <>
                <ShareStep key={`${file?.name ?? ""}:${file?.lastModified ?? 0}:${file?.size ?? 0}`} file={fixture ? null : file} signedIn={signedIn} />
                <PersonalStep
                  result={result}
                  signedIn={signedIn}
                  allowance={allowance}
                  proViewer={proViewer}
                  busy={busy}
                  onRequest={() => file && void submit(file, true)}
                />
              </>
            }
            footnote="The check runs the same matching your synced walks go through. Nothing about it is saved to your record."
          />
        )}
    </div>
  );
}
